import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher, useLocation, useSearchParams } from 'react-router';
import { parseURL } from '@archipro-website/top-navigation';

import type { SearchIndex } from '~/modules/search/type';
import { PROFESSIONAL_TAB_LOAD_MORE_ACTION } from '~/routes/actions.professional-tab-load-more';

interface LoadMoreResponse<TItem> {
    tiles: TItem[];
    hasMore: boolean;
    totalCount: number;
}

/**
 * Page-start boundaries for a loader payload that hydrated pages 1..page in
 * one query (the revamp tab deep-link behaviour): index 0 starts page 1,
 * index pageSize starts page 2, and so on.
 */
export const pageStartsForHydratedPages = (
    page: number,
    pageSize: number,
): Record<number, number> => {
    const starts: Record<number, number> = {};
    for (let p = 1; p <= Math.max(page, 1); p++) {
        starts[(p - 1) * pageSize] = p;
    }
    return starts;
};

interface UseTabLoadMoreParams<TItem> {
    /** Items for the loader-served first page. */
    initialItems: TItem[];
    /** Whether more pages exist beyond the initial page. */
    initialHasMore: boolean;
    /** The page number the initial items represent (the loader serves page 1). */
    initialPage: number;
    urlSegment: string;
    searchType: SearchIndex;
    /** Stable key used to dedupe appended items against ones already shown. */
    getKey: (item: TItem) => string | number;
    /**
     * Page-start boundaries of the initial items, item index → page. Deep
     * links hydrate pages 1..N in one loader query, so the initial items span
     * several pages — the caller derives the boundaries from its pagination
     * page size. Defaults to a single page starting at index 0.
     */
    initialPageStartByIndex?: Record<number, number>;
}

interface UseTabLoadMoreReturn<TItem> {
    /** Initial items plus every appended page. */
    items: TItem[];
    hasMore: boolean;
    isLoading: boolean;
    loadMore: () => void;
    /**
     * Item index → the (1-based) page that starts there, recorded as pages
     * append. Dedupe makes appended page lengths uneven, so page boundaries
     * can't be derived from a fixed page size. Feeds the viewport page
     * markers that keep `?page=N` accurate (useViewportPageSync).
     */
    pageStartByIndex: Record<number, number>;
}

/**
 * Client-side infinite-scroll pagination for the professional detail tabs.
 * Appends pages from {@link PROFESSIONAL_TAB_LOAD_MORE_ACTION} via `useFetcher`
 * (no URL navigation), forwarding the tab's current filter querystring so
 * appended pages match the active filters. Generic over the item shape so every
 * tab (projects, products, network, articles) shares one implementation.
 */
export function useTabLoadMore<TItem>({
    initialItems,
    initialHasMore,
    initialPage,
    urlSegment,
    searchType,
    getKey,
    initialPageStartByIndex,
}: UseTabLoadMoreParams<TItem>): UseTabLoadMoreReturn<TItem> {
    const fetcher = useFetcher<LoadMoreResponse<TItem>>();
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();

    // Items and their page-start indexes advance together on every append, so
    // they live in one state slot — a marker can never point past the items
    // it belongs to.
    const seedPageStarts = () => initialPageStartByIndex ?? { 0: initialPage };
    const [{ items, pageStartByIndex }, setLoaded] = useState<{
        items: TItem[];
        pageStartByIndex: Record<number, number>;
    }>({ items: initialItems, pageStartByIndex: seedPageStarts() });
    const [hasMore, setHasMore] = useState(initialHasMore);

    // True from the moment a page is requested until its items are on screen.
    // `fetcher.state` alone flips to idle one commit too early: the append
    // below is still only a scheduled state update at that point, so a scroll
    // sentinel measuring the page in that gap sees the pre-append height,
    // reads itself as still inside the lookahead zone, and fires a second
    // page the reader never asked for.
    const [isAppending, setIsAppending] = useState(false);

    // Page to request next — the loader already served `initialPage`.
    const nextPageRef = useRef(initialPage + 1);
    const loadedKeysRef = useRef(new Set(initialItems.map(getKey)));
    // Fetch identity counters: guard against processing a response twice and
    // against appending a fetch that a reset has since invalidated.
    const fetchCountRef = useRef(0);
    const processedFetchCountRef = useRef(0);
    // Set synchronously in loadMore so a re-firing scroll sentinel can't queue a
    // second request for the same page before the fetcher flips to "loading".
    const isFetchingRef = useRef(false);
    // Reset generation — bumped on every reset (filter, tab, or professional
    // change). A load-more stamps the generation it was fired under so a
    // response that lands after a reset is discarded as stale.
    const resetGenerationRef = useRef(0);
    const requestGenerationRef = useRef(0);

    // Reset when the professional, tab, or active filters change — keyed on
    // the URL, not the `initialItems` reference (Remix hands us a fresh array
    // on every background revalidation, which must not wipe appended pages).
    // The pathname matters beyond the querystring: legacy tab sort/filters
    // live in it as segments (sort_popular, category_*), and it also carries
    // the professional slug and tab. searchType distinguishes co-mounted tabs.
    const resetKey = `${pathname}::${searchType}::${searchParams.toString()}`;
    const resetKeyRef = useRef(resetKey);
    useEffect(() => {
        if (resetKeyRef.current === resetKey) return;
        resetKeyRef.current = resetKey;
        resetGenerationRef.current += 1;
        setLoaded({
            items: initialItems,
            pageStartByIndex: seedPageStarts(),
        });
        setHasMore(initialHasMore);
        nextPageRef.current = initialPage + 1;
        loadedKeysRef.current = new Set(initialItems.map(getKey));
        processedFetchCountRef.current = fetchCountRef.current;
        isFetchingRef.current = false;
        // A reset discards whatever page was in flight, so the tab is no
        // longer waiting on it.
        setIsAppending(false);
        // getKey is stable per call site; intentionally excluded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey, initialItems, initialHasMore, initialPage]);

    // Append fetched items once per fetch (deduped by key).
    useEffect(() => {
        if (fetcher.state !== 'idle') return;
        // The fetch has settled (even on error) — allow the next page to load.
        // Clearing `isAppending` here rather than beside the append batches it
        // with `setLoaded` into one commit, so the tiles are in the DOM by the
        // time anything observes the tab as no longer loading. It also covers
        // the stale / duplicate early returns below, where no tiles are coming.
        isFetchingRef.current = false;
        setIsAppending(false);
        if (
            !fetcher.data ||
            // Drop a response from a fetch fired before the latest reset — its
            // tiles belong to a previous professional / tab / filter set.
            requestGenerationRef.current !== resetGenerationRef.current ||
            processedFetchCountRef.current >= fetchCountRef.current
        ) {
            return;
        }
        processedFetchCountRef.current = fetchCountRef.current;

        // `useFetcher` jsonifies the response; the action serialises the same
        // tile shape the loader does, so cast back to the caller's item type.
        const incoming = fetcher.data.tiles as TItem[];
        const unique = incoming.filter((item) => {
            const key = getKey(item);
            if (loadedKeysRef.current.has(key)) return false;
            loadedKeysRef.current.add(key);
            return true;
        });

        // Advance the page and trust the server's `hasMore` even when a page
        // came back fully duplicate (ES re-ranking can overlap pages) — a
        // transient duplicate must not permanently stop the scroll.
        // `?page=N` is NOT written here: the sections' viewport page markers
        // keep it in step with the page the reader actually sees
        // (useViewportPageSync) — an append-time write runs ahead of the
        // viewport under the infinite-scroll lookahead.
        const loadedPage = nextPageRef.current;
        nextPageRef.current += 1;
        if (unique.length > 0) {
            setLoaded((prev) => ({
                items: [...prev.items, ...unique],
                pageStartByIndex: {
                    ...prev.pageStartByIndex,
                    [prev.items.length]: loadedPage,
                },
            }));
        }
        setHasMore(fetcher.data.hasMore);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher.state, fetcher.data]);

    const loadMore = useCallback(() => {
        if (isFetchingRef.current || fetcher.state !== 'idle' || !hasMore) {
            return;
        }
        isFetchingRef.current = true;
        setIsAppending(true);
        requestGenerationRef.current = resetGenerationRef.current;

        const params = new URLSearchParams(searchParams);
        // Forward path-encoded params (the legacy sort_/category_ segments,
        // e.g. /case-studies/sort_popular) as query params too — the action
        // rebuilds its ES query from its own URL, which has no tab path to
        // read them from, so without this later pages ignore the active sort.
        const { params: pathParams } = parseURL(pathname);
        for (const [key, value] of Object.entries(pathParams)) {
            if (params.has(key)) continue;
            for (const single of Array.isArray(value) ? value : [value]) {
                params.append(key, String(single));
            }
        }
        params.set('urlSegment', urlSegment);
        params.set('searchType', searchType);
        params.set('page', String(nextPageRef.current));

        fetchCountRef.current += 1;
        fetcher.load(
            `${PROFESSIONAL_TAB_LOAD_MORE_ACTION}?${params.toString()}`,
        );
    }, [fetcher, hasMore, searchParams, pathname, urlSegment, searchType]);

    return {
        items,
        hasMore,
        isLoading: fetcher.state !== 'idle' || isAppending,
        loadMore,
        pageStartByIndex,
    };
}
